import * as React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { MetaRoute, RoutesConfig } from './type';
import Error from 'common/components/Error';

/**
 * 获取默认重定向路由
 */
const defaultRoute: (routes: Array<MetaRoute>) => MetaRoute = (routes: Array<MetaRoute>) => {
    let [match] = routes.filter(({ sort }) => parseInt(sort) >= 0);
    // 强制修改密码默认跳到个人设置
    if (routes.length === 1 && (routes[0].key === 'ACCOUNT' || routes[0].key === 'PERSONAL')) match = routes[0];
    return match || {} as any;
};

/**
 * 权限信息处理
 */
const getAuthorities: any = (routes: Array<MetaRoute>) => {
    return (routes || [])
        .reduce((prev, { key, children }) => ({
            ...prev,
            [key.match(/[A-Za-z0-9]+$/)[0]]: (children && children.length) ? getAuthorities(children) : true
        }), {});
};

// 需要剔除的路由(包括其子路由)
const filterPasthnames = ['personal', 'team'];
const filterPathKey: Record<string, string> = {
    personal: 'CLIENT_MANAGER_LIST_DETAIL',
    team: 'TEAM_MANAGER_CLIENT_DETAIL'
};
const filterPathKeys = Object.values(filterPathKey);
const routePathname: Record<string, string> = {
    personal: '/client/manager/list/detail',
    team: '/team/manager/client/detail',
};
const routeLoaders: Record<string, string> = {
    personal: 'Client.Manager.List.Detail',
    team: 'Team.Manager.Client.Detail',
};
/**
 * 获取被剔除路由的路径，及权限
 */
function getRoutePath(pathname: string, routes: Record<string, any>) {
    let route: string;
    let key;
    if (pathname.includes(routePathname.personal)) {
        key = 'personal';
        route = routePathname.personal + '/:id';
    } else if (pathname.includes(routePathname.team)) {
        key = 'team';
        route = routePathname.team + '/:id';
    }
    const pathKey = filterPathKey[key];
    let isPermission = false;
    if (pathKey) {
        const [key1, key2, key3] = pathKey.split('_');
        isPermission = routes.find(({ key }: any) => key === key1)?.children
            .find(({ key }: any) => key === `${key1}_${key2}`)?.children
            .find(({ key }: any) => key === `${key1}_${key2}_${key3}`)?.children
            .find(({ key }: any) => key === pathKey);
    }
    return { route, routeLoader: routeLoaders[key], isPermission };
}

/**
 * 声明路由
 */
type DeclareRoutes = (config: RoutesConfig, routeProps: any, isPermission?: boolean, renderPageFooter?: Function) => JSX.Element;
const declareRoutes: DeclareRoutes = ({ routes, loaders, presets }, routeProps, isPermission, renderPageFooter) =>
    <Switch>
        {routes
            .filter(({ key, component, children }: MetaRoute) => {
                if (filterPathKeys.includes(key)) return false;
                return component || children;
            })
            .map(({ key, href, exact, component, children }: MetaRoute) => [
                component && <Route key={key} path={href} exact={typeof exact !== 'boolean' || exact} render={props => {
                    const Module = Error(loaders[component]);
                    return <Module {...{
                        ...routeProps,
                        renderPageFooter,
                        authorities: getAuthorities(children),
                        pageKey: key,
                        ...(presets || {})
                    }} {...props} />;
                }} />,
                children && children.filter(({ href, component }) => href || component).length
                && <Route key={key} path={href} render={() => declareRoutes({
                    routes: children,
                    loaders,
                    presets
                }, routeProps, isPermission, renderPageFooter)} />
            ])}
        <Route render={(routeProps) => {
            const pathname = routeProps?.location?.pathname;
            if (filterPasthnames.some(i => pathname.includes(routePathname[i]))) {
                if (isPermission) return null;
                if (routeProps?.match?.params?.id) {
                    let toUri = (defaultRoute(routes)).href || '/';
                    toUri = toUri.replace(':id', routeProps.match.params.id);
                    return <Redirect to={toUri} />;
                }
            }
            return <Redirect to={(defaultRoute(routes)).href || '/'} />;
        }} />
    </Switch>;

/**
 * 客户详情页默认路由
 */
const clientDetailDefault = (config: RoutesConfig, routeProps: any, { route, routeLoader, isPermission }: any) => {
    const Module = config.loaders[routeLoader];
    return route && isPermission && !!Module && <Route path={route} render={props => {
        return <Module {...{
            ...routeProps,
        }} {...props} />;
    }} />;
};

// 客户详情页路由
const clientDetailRoutes: DeclareRoutes = ({ routes, loaders, presets }, routeProps) => {
    return <Switch>
        {routes.filter(({ component, children }: MetaRoute) => component || children)
            .map(({ key, href, exact, component, children }: MetaRoute) => [
                component && loaders[component] && <Route key={key} path={href} exact={typeof exact !== 'boolean' || exact} render={props => {
                    const Module = Error(loaders[component]);
                    return <Module {...{
                        ...routeProps,
                        authorities: getAuthorities(children),
                        ...(presets || {})
                    }} {...props} />;
                }} />,
                children && children.filter(({ href, component }) => href || component).length
                && <Route key={key} path={href} render={() => declareRoutes({
                    routes: children,
                    loaders,
                    presets
                }, routeProps)} />
            ])}
    </Switch>;
};

interface iProps {
    location?: any;
    config: RoutesConfig;
    routeProps?: any;
    renderPageFooter?: Function;
    isClientDetail?: boolean;
}

export default class Router extends React.Component<iProps> {

    // 判断当前页面与上一页面是否包含指定路径
    isEqPathname = (pathname: string, nextPathname: string) => (diffPathname: string) => {
        return pathname.includes(diffPathname) && nextPathname.includes(diffPathname);
    }

    shouldComponentUpdate(nextProps: iProps) {
        if (nextProps.routeProps) {
            if (nextProps.routeProps?.isLoaded) {
                return true;
            }
        } else {
            const isEqPathname = this.isEqPathname(this.props.location.pathname, nextProps.location.pathname);
            if (filterPasthnames.some(i => isEqPathname(routePathname[i]))) return false;
        }
        return this.props.location.key !== nextProps.location.key;
    }

    render() {
        const { config, routeProps = {}, renderPageFooter, location } = this.props;
        if (this.props.isClientDetail) return clientDetailRoutes(config, routeProps);
        const { route, routeLoader, isPermission } = getRoutePath(location.pathname, config.routes);
        return <>
            {clientDetailDefault(config, routeProps, { route, routeLoader, isPermission })}
            {declareRoutes({
                routes: config.routes,
                loaders: config.loaders,
                presets: { ...(config.presets || {}), location }
            }, routeProps, isPermission, renderPageFooter)}
        </>;
    }
}
